import {
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ApplicationRoutes } from '../../../../application/constants/routes';
import { useI18n } from '../../../../application/language/useI18n';
import { ILearningProjectRatingModel } from '../../../../service/report/interface/ILearningProjectRatingModel';
import { getPersistedRatingsState, setPersistedRatingsState } from './persistence';
import { RatingProgress } from './RatingProgress';

type IProjectRatingsSortField = 'projectName' | 'averageProgress' | 'usersCount' | 'averageScore';
type IOrder = 'asc' | 'desc';
type IProjectRatingsTableState = {
  order: IOrder;
  orderBy: IProjectRatingsSortField;
  page: number;
  rowsPerPage: number;
};

const PROJECT_RATINGS_TABLE_STATE_KEY = 'analytics/ratings/projects/table';

type IProjectRatingsTableProperties = {
  rows: ILearningProjectRatingModel[];
};

const sortRows = (rows: ILearningProjectRatingModel[], orderBy: IProjectRatingsSortField, order: IOrder) => {
  return [...rows].sort((a, b) => {
    const direction = order === 'asc' ? 1 : -1;

    switch (orderBy) {
      case 'projectName':
        return a.projectName.localeCompare(b.projectName) * direction;
      case 'averageProgress':
        return (a.averageProgress - b.averageProgress) * direction;
      case 'usersCount':
        return (a.usersCount - b.usersCount) * direction;
      case 'averageScore':
        return (a.averageScore - b.averageScore) * direction;
      default:
        return 0;
    }
  });
};

export const ProjectRatingsTable = ({ rows }: IProjectRatingsTableProperties) => {
  const { translate } = useI18n();
  const persistedState = getPersistedRatingsState<IProjectRatingsTableState>(PROJECT_RATINGS_TABLE_STATE_KEY, {
    order: 'desc',
    orderBy: 'averageProgress',
    page: 0,
    rowsPerPage: 10,
  });
  const [order, setOrder] = useState<IOrder>(persistedState.order);
  const [orderBy, setOrderBy] = useState<IProjectRatingsSortField>(persistedState.orderBy);
  const [page, setPage] = useState(persistedState.page);
  const [rowsPerPage, setRowsPerPage] = useState(persistedState.rowsPerPage);

  useEffect(() => {
    setPersistedRatingsState(PROJECT_RATINGS_TABLE_STATE_KEY, {
      order,
      orderBy,
      page,
      rowsPerPage,
    });
  }, [order, orderBy, page, rowsPerPage]);

  const handleSort = (field: IProjectRatingsSortField) => {
    setOrder((prev) => (orderBy === field && prev === 'asc' ? 'desc' : 'asc'));
    setOrderBy(field);
  };

  const sortedRows = sortRows(rows, orderBy, order);
  const safePage = page * rowsPerPage >= sortedRows.length && page > 0 ? 0 : page;
  const visibleRows = sortedRows.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);

  return (
    <>
      <TableContainer component={Paper} sx={{ maxHeight: 520, overflowY: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }}>№</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'projectName'}
                  direction={orderBy === 'projectName' ? order : 'asc'}
                  onClick={() => handleSort('projectName')}
                >
                  {translate('analytics.ratings.projects.name')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'averageProgress'}
                  direction={orderBy === 'averageProgress' ? order : 'asc'}
                  onClick={() => handleSort('averageProgress')}
                >
                  {translate('analytics.ratings.projects.averageProgress')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'usersCount'}
                  direction={orderBy === 'usersCount' ? order : 'asc'}
                  onClick={() => handleSort('usersCount')}
                >
                  {translate('analytics.ratings.projects.usersCount')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'averageScore'}
                  direction={orderBy === 'averageScore' ? order : 'asc'}
                  onClick={() => handleSort('averageScore')}
                >
                  {translate('analytics.ratings.projects.averageScore')}
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, index) => (
              <TableRow key={row.projectId}>
                <TableCell>{safePage * rowsPerPage + index + 1}</TableCell>
                <TableCell>
                  <Link
                    component={RouterLink}
                    to={ApplicationRoutes.AnalyticsProject.replace(':projectId', row.projectId)}
                    underline="hover"
                    color="inherit"
                  >
                    {row.projectName}
                  </Link>
                </TableCell>
                <TableCell>
                  <RatingProgress value={row.averageProgress} label={`${row.averageProgress}% (${row.passedUnitsCount}/${row.totalUnitsCount})`} threeColors />
                </TableCell>
                <TableCell>{row.usersCount}</TableCell>
                <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{`${row.averageScore} / ${row.averageMaxScore}`}</TableCell>
              </TableRow>
            ))}
            {!visibleRows.length && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {translate('analytics.ratings.noData')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={safePage}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25]}
        labelRowsPerPage={translate('analytics.ratings.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${translate('analytics.ratings.of')} ${count}`}
        getItemAriaLabel={(type) =>
          type === 'next' ? translate('analytics.ratings.nextPage') : translate('analytics.ratings.previousPage')
        }
      />
    </>
  );
};
